import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { userSelector, loginUser, clearState, setNeedFirstLoad } from './UserSlice';
import { useHistory } from 'react-router-dom';

import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import Link from '@mui/material/Link';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import useMediaQuery from '@mui/material/useMediaQuery';
import { QRCode } from 'antd';
import { alpha, useTheme } from '@mui/material/styles';
import RouteMapBackground from './RouteMapBackground';

const ANDROID_APP_URL = 'https://play.google.com/store/apps/details?id=com.roadmatics.roadrnext';
const IOS_APP_URL = 'https://apps.apple.com/in/app/roadrnext/id1476699381';
const OLD_APK_URL = '';
const SUPPORT_PHONE_TEXT = '+91 93728 00056';
const SUPPORT_PHONE_LINK = 'tel:+919372800056';
const SUPPORT_EMAIL = 'info@roadmatics.com';
const SUPPORT_EMAIL_LINK = 'mailto:info@roadmatics.com';
const BRAND_NAME = 'Roadrnext 3.0';
const SHARP_RADIUS = '6px';
const WEBSITE_URL = 'https://www.roadmatics.com/';

function Copyright() {
    return (
        <Typography
            variant="caption"
            sx={{
                color: 'text.secondary',
                textAlign: 'center',
                display: 'block',
            }}
        >
            {'Copyright © '}
            <Link color="inherit" href="https://www.roadmatics.com/" target="_blank" rel="noreferrer">
                Roadmatics Technologies Private Limited
            </Link>{' '}
            {new Date().getFullYear()}
            {'.'}
        </Typography>
    );
}

function StoreBadgeLink({ href, src, alt, maxWidth = 168, offsetY = 0, minHeight = 52 }) {
    return (
        <Link
            href={href}
            target="_blank"
            rel="noreferrer"
            underline="none"
            sx={{
                display: 'inline-flex',
                alignItems: 'flex-end',
                justifyContent: 'center',
                minHeight,
            }}
        >
            <Box
                component="img"
                src={src}
                alt={alt}
                loading="lazy"
                sx={{
                    width: '100%',
                    maxWidth,
                    height: 'auto',
                    display: 'block',
                    transform: offsetY ? `translateY(${offsetY}px)` : 'none',
                }}
            />
        </Link>
    );
}

function DownloadOldApkLink({ centered = false }) {
    const sharedSx = {
        fontSize: '0.77rem',
        fontWeight: 500,
        color: 'text.secondary',
        textDecorationColor: 'text.disabled',
    };

    return OLD_APK_URL ? (
        <Link
            href={OLD_APK_URL}
            underline="hover"
            sx={{
                ...sharedSx,
                display: 'inline-flex',
                justifyContent: centered ? 'center' : 'flex-start',
            }}
        >
            Download old APK
        </Link>
    ) : (
        <Typography
            variant="caption"
            sx={{
                ...sharedSx,
                display: 'block',
                textAlign: centered ? 'center' : 'left',
            }}
        >
            Download old APK
        </Typography>
    );
}

function AppDownloadPanel({ mobile = false }) {
    const theme = useTheme();

    const qrCard = (
        title,
        qrValue,
        href,
        badgeSrc,
        badgeAlt,
    ) => (
        <Box
            sx={{
                flex: '1 1 0',
                minWidth: 0,
                borderRadius: SHARP_RADIUS,
                p: 1.5,
                backgroundColor: alpha('#ffffff', 0.92),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                boxShadow: `0 12px 28px ${alpha('#0f274f', 0.08)}`,
            }}
        >
            
            <Box
                sx={{
                    mt: 1.25,
                    p: 1.25,
                    borderRadius: SHARP_RADIUS,
                    backgroundColor: '#ffffff',
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <QRCode
                    value={qrValue}
                    type="svg"
                    size={104}
                    bordered={false}
                    color={theme.palette.text.primary}
                    bgColor="#ffffff"
                />
            </Box>
            <Box sx={{ mt: 1.2, display: 'flex', justifyContent: 'center' }}>
                <StoreBadgeLink
                    href={href}
                    src={badgeSrc}
                    alt={badgeAlt}
                    maxWidth={132}
                    minHeight={54}
                    offsetY={title === 'Android' ? 0 : -2}
                />
            </Box>
        </Box>
    );

    if (mobile) {
        return (
            <Box sx={{ width: '100%' }}>
                <Typography
                    variant="subtitle1"
                    sx={{
                        fontWeight: 700,
                        color: 'text.primary',
                        mb: 1.25,
                    }}
                >
                    Get the mobile app
                </Typography>
                <Stack spacing={1.25}>
                    <StoreBadgeLink
                        href={ANDROID_APP_URL}
                        src="/assets/store-badges/google-play-badge.png"
                        alt="Get it on Google Play"
                        maxWidth={190}
                        minHeight={54}
                        offsetY={-4}
                    />
                    <StoreBadgeLink
                        href={IOS_APP_URL}
                        src="/assets/store-badges/app-store-badge.svg"
                        alt="Download on the App Store"
                        maxWidth={190}
                        minHeight={54}
                    />
                </Stack>
                <Box sx={{ mt: 1.25 }}>
                    <DownloadOldApkLink />
                </Box>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                width: '100%',
                maxWidth: 442,
                borderRadius: SHARP_RADIUS,
                p: { xs: 2, md: 2.25 },
                backgroundColor: alpha('#ffffff', 0.66),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                backdropFilter: 'blur(12px)',
                boxShadow: `0 20px 42px ${alpha('#0f274f', 0.08)}`,
            }}
        >
            <Typography
                variant="subtitle1"
                sx={{
                    fontWeight: 700,
                    color: 'text.primary',
                }}
            >
                Get the mobile app
            </Typography>
            
            <Stack
                direction="row"
                spacing={1.5}
                sx={{
                    mt: 1.5,
                    alignItems: 'stretch',
                }}
            >
                {qrCard(
                    'Android',
                    ANDROID_APP_URL,
                    ANDROID_APP_URL,
                    '/assets/store-badges/google-play-badge.png',
                    'Get it on Google Play',
                )}
                {qrCard(
                    'iPhone',
                    IOS_APP_URL,
                    IOS_APP_URL,
                    '/assets/store-badges/app-store-badge.svg',
                    'Download on the App Store',
                )}
            </Stack>
            <Box sx={{ mt: 1.5 }}>
                <DownloadOldApkLink />
            </Box>
        </Box>
    );
}

export default function SignIn() {
    const dispatch = useDispatch();
    const history = useHistory();
    const theme = useTheme();
    const isCompact = useMediaQuery(theme.breakpoints.down('md'));
    const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

    const { isFetching, isSuccess, isError, errorMessage } = useSelector(userSelector);

    const [ilogusername, setIlogusername] = useState('');
    const [ilogpassword, setIlogpassword] = useState('');

    const onUserChange = (e) => {
        if (isError) {
            dispatch(clearState());
        }
        setIlogusername(e.target.value);
    };

    const onPasswordChange = (e) => {
        if (isError) {
            dispatch(clearState());
        }
        setIlogpassword(e.target.value);
    };

    function onSubmit(e) {
        e.preventDefault();
        if (isFetching || !ilogusername.trim() || !ilogpassword) {
            return;
        }
        dispatch(clearState());
        dispatch(loginUser({ ilogusername, ilogpassword }));
    }

    useEffect(() => {
        if (isSuccess) {
            dispatch(clearState());
            dispatch(setNeedFirstLoad(true));
            history.push('/secured/livetracking');
        }
    }, [isSuccess, dispatch, history]);

    const buttonDisabled = isFetching || !ilogusername.trim() || !ilogpassword;
    const reduceMotion = prefersReducedMotion || isCompact;

    return (
        <Box
            component="main"
            sx={{
                position: 'relative',
                minHeight: '100vh',
                backgroundColor: '#f4f8fc',
                overflowX: 'hidden',
            }}
        >
            <CssBaseline />
            <RouteMapBackground compact={isCompact} reduceMotion={reduceMotion} />

            <Box
                sx={{
                    position: 'relative',
                    zIndex: 1,
                    minHeight: '100vh',
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                }}
            >
                <Box
                    component="section"
                    sx={{
                        flexBasis: { md: '58%' },
                        px: { xs: 3, sm: 4, md: 6, lg: 8 },
                        pt: { xs: 4, md: 5.5 },
                        pb: { xs: 2, md: 5.5 },
                        display: 'flex',
                    }}
                >
                    <Stack
                        spacing={{ xs: 3, md: 5 }}
                        justifyContent={{ md: 'space-between' }}
                        sx={{
                            width: '100%',
                            maxWidth: { md: 760, lg: 820, xl: 860 },
                        }}
                    >
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <Box
                                component="img"
                                src="/cuboid.png"
                                alt="Roadrnext cube logo"
                                sx={{
                                    width: { xs: 58, sm: 72 },
                                    height: 'auto',
                                    display: 'block',
                                    flexShrink: 0,
                                }}
                            />
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    minHeight: { xs: 58, sm: 72 },
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontFamily: '"Barlow Condensed", sans-serif',
                                        fontWeight: 700,
                                        fontSize: { xs: '2rem', sm: '2.55rem' },
                                        lineHeight: 1,
                                        color: '#193766',
                                    }}
                                >
                                    {BRAND_NAME}
                                </Typography>
                            </Box>
                        </Stack>

                        <Stack
                            spacing={{ xs: 3, md: 3.5 }}
                            sx={{
                                width: '100%',
                                pt: { xs: 0.5, md: 1.5 },
                            }}
                        >
                            <Box sx={{ maxWidth: { xs: 620, md: 660, xl: 700 } }}>
                                <Box sx={{ maxWidth: { xs: 520, md: 620, xl: 680 } }}>
                                    <Typography
                                        component="h1"
                                        sx={{
                                            fontFamily: '"Barlow Condensed", sans-serif',
                                            fontWeight: 700,
                                            fontSize: { xs: '3.05rem', sm: '4.15rem', md: '4.45rem', lg: '4.9rem', xl: '5.2rem' },
                                            lineHeight: 0.92,
                                            letterSpacing: '0.01em',
                                            color: '#18345f',
                                            whiteSpace: { lg: 'nowrap' },
                                        }}
                                    >
                                        TRACK EVERYTHING.
                                        <Box component="span" sx={{ display: 'block', color: theme.palette.primary.main }}>
                                            LOSE NOTHING.
                                        </Box>
                                    </Typography>

                                    
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            mt: 2,
                                            maxWidth: 540,
                                            fontFamily: '"Montserrat", sans-serif',
                                            fontSize: { xs: '1.02rem', sm: '1.14rem', md: '1.24rem' },
                                            lineHeight: 1.55,
                                            fontWeight: 400,
                                            color: alpha('#18345f', 0.86),
                                        }}
                                    >
                                        Location, Diesel, Temperature, Generator & AIS140 VLTD
                                    </Typography>
                                </Box>
                            </Box>

                            {!isCompact && (
                                <Box
                                    sx={{
                                        width: '100%',
                                        maxWidth: 460,
                                        pt: { md: 0.5, lg: 1 },
                                    }}
                                >
                                    <AppDownloadPanel />
                                </Box>
                            )}
                        </Stack>
                    </Stack>
                </Box>

                <Box
                    component="section"
                    sx={{
                        position: 'relative',
                        flexBasis: { md: '42%' },
                        px: { xs: 3, sm: 4, md: 5, lg: 6 },
                        pb: { xs: 4, md: 3.5 },
                        pt: { xs: 1, md: 0 },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: {
                            md: `linear-gradient(90deg, ${alpha('#ffffff', 0)} 0%, ${alpha('#ffffff', 0.24)} 18%, ${alpha('#ffffff', 0.68)} 58%, ${alpha('#ffffff', 0.88)} 100%)`,
                        },
                    }}
                >
                    <Stack spacing={2} sx={{ width: '100%', maxWidth: 470, alignItems: 'center' }}>
                        <Card
                            elevation={0}
                            sx={{
                                width: '100%',
                                maxWidth: 440,
                                borderRadius: SHARP_RADIUS,
                                backgroundColor: alpha('#ffffff', 0.92),
                                backdropFilter: 'blur(14px)',
                                border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                                boxShadow: `0 26px 54px ${alpha('#0f274f', 0.14)}`,
                            }}
                        >
                            <CardContent sx={{ p: { xs: 3, sm: 4.5 } }}>
                                <Typography
                                    component="h2"
                                    sx={{
                                        fontFamily: '"Montserrat", sans-serif',
                                        fontWeight: 700,
                                        fontSize: { xs: '2rem', sm: '2.35rem' },
                                        color: '#25324c',
                                        mb: 3,
                                    }}
                                >
                                    Sign In
                                </Typography>

                                {isError && (
                                    <Box
                                        role="alert"
                                        sx={{
                                            mb: 2.25,
                                            px: 2,
                                            py: 1.25,
                                            borderRadius: SHARP_RADIUS,
                                            border: `1px solid ${alpha(theme.palette.error.main, 0.24)}`,
                                            backgroundColor: alpha(theme.palette.error.light, 0.12),
                                        }}
                                    >
                                        <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: theme.palette.error.dark,
                                                    fontWeight: 500,
                                                }}
                                            >
                                                {errorMessage || 'Sign in failed. Please try again.'}
                                            </Typography>
                                            <Link
                                                component="button"
                                                type="button"
                                                underline="hover"
                                                onClick={() => dispatch(clearState())}
                                                sx={{
                                                    color: theme.palette.error.dark,
                                                    fontSize: '0.84rem',
                                                    fontWeight: 600,
                                                }}
                                            >
                                                Dismiss
                                            </Link>
                                        </Stack>
                                    </Box>
                                )}

                                <Box component="form" onSubmit={onSubmit}>
                                    <Stack spacing={2}>
                                        <TextField
                                            required
                                            fullWidth
                                            autoFocus={!isCompact}
                                            id="username"
                                            label="Username"
                                            name="username"
                                            autoComplete="username"
                                            value={ilogusername}
                                            onChange={onUserChange}
                                            type="text"
                                            disabled={isFetching}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: SHARP_RADIUS,
                                                    backgroundColor: alpha(theme.palette.primary.light, 0.08),
                                                    '& fieldset': {
                                                        borderColor: alpha(theme.palette.primary.main, 0.18),
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: alpha(theme.palette.primary.main, 0.35),
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: theme.palette.primary.main,
                                                        borderWidth: 2,
                                                    },
                                                },
                                                '& .MuiInputBase-input': {
                                                    py: 1.6,
                                                    fontSize: '1.08rem',
                                                    fontWeight: 500,
                                                },
                                                '& .MuiInputLabel-root': {
                                                    fontWeight: 500,
                                                },
                                            }}
                                        />
                                        <TextField
                                            required
                                            fullWidth
                                            name="password"
                                            label="Password"
                                            type="password"
                                            id="password"
                                            autoComplete="current-password"
                                            value={ilogpassword}
                                            onChange={onPasswordChange}
                                            disabled={isFetching}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: SHARP_RADIUS,
                                                    backgroundColor: alpha(theme.palette.primary.light, 0.08),
                                                    '& fieldset': {
                                                        borderColor: alpha(theme.palette.primary.main, 0.18),
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: alpha(theme.palette.primary.main, 0.35),
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: theme.palette.primary.main,
                                                        borderWidth: 2,
                                                    },
                                                },
                                                '& .MuiInputBase-input': {
                                                    py: 1.6,
                                                    fontSize: '1.08rem',
                                                    fontWeight: 500,
                                                },
                                                '& .MuiInputLabel-root': {
                                                    fontWeight: 500,
                                                },
                                            }}
                                        />
                                        <Button
                                            type="submit"
                                            fullWidth
                                            variant="contained"
                                            color="primary"
                                            disabled={buttonDisabled}
                                            sx={{
                                                mt: 0.5,
                                                minHeight: 56,
                                                borderRadius: SHARP_RADIUS,
                                                boxShadow: `0 14px 30px ${alpha(theme.palette.primary.main, 0.28)}`,
                                                fontSize: '1rem',
                                                fontWeight: 700,
                                                letterSpacing: '0.08em',
                                            }}
                                        >
                                            {isFetching ? (
                                                <CircularProgress
                                                    size={22}
                                                    thickness={4.8}
                                                    sx={{ color: '#ffffff' }}
                                                />
                                            ) : (
                                                'Sign In'
                                            )}
                                        </Button>
                                    </Stack>
                                </Box>

                                <Box
                                    sx={{
                                        mt: 3,
                                        pt: 2.5,
                                        borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            maxWidth: 360,
                                            mx: 'auto',
                                            px: 2,
                                            py: 1.75,
                                            borderRadius: SHARP_RADIUS,
                                            backgroundColor: alpha(theme.palette.primary.light, 0.08),
                                            border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                                            boxShadow: `0 10px 24px ${alpha('#0f274f', 0.05)}`,
                                            display: 'grid',
                                            gridTemplateColumns: '120px minmax(0, 1fr)',
                                            rowGap: 0.8,
                                            columnGap: 1.5,
                                            alignItems: 'baseline',
                                            width: '100%',
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: 'text.secondary',
                                                fontWeight: 600,
                                                textAlign: 'left',
                                                lineHeight: 1.5,
                                            }}
                                        >
                                            Support
                                        </Typography>
                                        <Link
                                            href={SUPPORT_PHONE_LINK}
                                            underline="hover"
                                            sx={{
                                                fontWeight: 600,
                                                fontSize: '0.95rem',
                                                lineHeight: 1.5,
                                                textAlign: 'left',
                                            }}
                                        >
                                            {SUPPORT_PHONE_TEXT}
                                        </Link>

                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: 'text.secondary',
                                                fontWeight: 600,
                                                textAlign: 'left',
                                                lineHeight: 1.5,
                                            }}
                                        >
                                            Email
                                        </Typography>
                                        <Link
                                            href={SUPPORT_EMAIL_LINK}
                                            underline="hover"
                                            sx={{
                                                fontWeight: 600,
                                                fontSize: '0.95rem',
                                                lineHeight: 1.5,
                                                textAlign: 'left',
                                            }}
                                        >
                                            {SUPPORT_EMAIL}
                                        </Link>

                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: 'text.secondary',
                                                fontWeight: 600,
                                                textAlign: 'left',
                                                lineHeight: 1.5,
                                            }}
                                        >
                                            Web
                                        </Typography>
                                        <Link
                                            href={WEBSITE_URL}
                                            target="_blank"
                                            rel="noreferrer"
                                            underline="hover"
                                            sx={{
                                                fontWeight: 600,
                                                fontSize: '0.95rem',
                                                lineHeight: 1.5,
                                                textAlign: 'left',
                                            }}
                                        >
                                            www.roadmatics.com
                                        </Link>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>

                        {isCompact && (
                            <Box
                                sx={{
                                    width: '100%',
                                    borderRadius: SHARP_RADIUS,
                                    p: 2.5,
                                    backgroundColor: alpha('#ffffff', 0.74),
                                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                                    boxShadow: `0 18px 36px ${alpha('#0f274f', 0.08)}`,
                                }}
                            >
                                <AppDownloadPanel mobile />
                            </Box>
                        )}

                        <Box sx={{ pt: 0.5 }}>
                            <Copyright />
                        </Box>
                    </Stack>
                </Box>
            </Box>
        </Box>
    );
}
